import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  CakeIcon,
  UserGroupIcon,
  HeartIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  MegaphoneIcon,
  ClipboardDocumentCheckIcon,
  UserPlusIcon,
  ShieldCheckIcon,
  HomeIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  ClockIcon,
  GlobeAltIcon,
  NewspaperIcon,
  ChatBubbleLeftRightIcon,
  DocumentCheckIcon
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import apiClient from '../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import { getDogImageUrl } from '../utils/imageUtils';

// Create a typed icon component
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface MemberDetailProps {
  member: any;
  profileImage?: string;
  memberPayments?: any;
  dogsStatus?: {
    hasDogs: boolean;
    dogsPaid: boolean;
    count: number;
    dogs?: Array<{
      name: string;
      id?: string;
      valid_insurance?: boolean;
      valid_tollwut?: boolean;
    }>;
  };
  onClose: () => void;
  onSave?: (member: any) => Promise<{ success: boolean; error?: string }>;
  onDelete?: (memberId: string) => void;
}

const MemberDetail: React.FC<MemberDetailProps> = ({
  member: initialMember,
  profileImage,
  memberPayments,
  dogsStatus,
  onClose,
  onSave,
  onDelete
}) => {
  const [member, setMember] = useState(initialMember);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [memberRoles, setMemberRoles] = useState<number[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [dogImageErrors, setDogImageErrors] = useState<Record<string, boolean>>({});
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [activateConfirmModal, setActivateConfirmModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showBookingHistory, setShowBookingHistory] = useState(false);
  const [bookingHistory, setBookingHistory] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  useEffect(() => {
    const formattedMember = {
      ...initialMember,
      geb_am: initialMember.geb_am ? initialMember.geb_am.split('T')[0] : '',
      eintritt: initialMember.eintritt ? initialMember.eintritt.split('T')[0] : ''
    };
    setMember(formattedMember);
    setImageError(false);
    if (initialMember.roles) {
      setMemberRoles(initialMember.roles.map((r: any) => r.id));
    }
  }, [initialMember]);

  useEffect(() => {
    if (initialMember) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      setTimeout(() => setIsVisible(true), 10);
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [initialMember]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  useEffect(() => {
    const fetchAvailableRoles = async () => {
      try {
        const response = await apiClient.get('/api/roles/available');
        if (response.data.success && Array.isArray(response.data.roles)) {
          setAvailableRoles(response.data.roles);
        }
      } catch (error) {
        console.error('Error fetching available roles:', error);
      }
    };
    fetchAvailableRoles();
  }, []);

  // Fetch booking history when modal opens
  useEffect(() => {
    const fetchBookingHistory = async () => {
      if (!showBookingHistory || !member?.mg_nr) return;

      setLoadingBookings(true);
      try {
        const response = await apiClient.get(`/api/events/user-events/${member.mg_nr}`);
        // eventsBooked contains all events where this member is registered
        const bookings = (response.data.eventsBooked || []).map((event: any) => ({
          id: event.id,
          title: event.title,
          event_date: event.start,
          event_time: event.start_time,
          end_time: event.end_time,
          trainer_name: event.creator_name,
          cotrainer_name: event.cotrainer_name,
          participant_count: event.participantCount,
          status: 'confirmed'
        }));
        // Sort by date descending (newest first)
        bookings.sort((a: any, b: any) => new Date(b.event_date).getTime() - new Date(a.event_date).getTime());
        setBookingHistory(bookings);
      } catch (error) {
        console.error('Error fetching booking history:', error);
        setBookingHistory([]);
      } finally {
        setLoadingBookings(false);
      }
    };
    fetchBookingHistory();
  }, [showBookingHistory, member?.mg_nr]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setMember((prev: any) => ({
        ...prev,
        [name]: target.checked ? 1 : 0
      }));
    } else {
      setMember((prev: any) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const toggleRole = (roleId: number) => {
    setMemberRoles(prev => {
      if (prev.includes(roleId)) {
        return prev.filter(id => id !== roleId);
      } else {
        return [...prev, roleId];
      }
    });
  };

  const handleSave = async () => {
    if (!onSave) return;
    setLoading(true);

    try {
      const memberToSave = {
        ...member,
        geb_am: member.geb_am ? member.geb_am.split('T')[0] : null,
        eintritt: member.eintritt ? member.eintritt.split('T')[0] : null
      };

      const result = await onSave(memberToSave);
      if (result.success) {
        try {
          await apiClient.put(`/api/roles/member/${member.mg_nr}`, {
            role_ids: memberRoles
          });

          const updatedRoles = availableRoles.filter(r => memberRoles.includes(r.id));
          setMember((prev: any) => ({
            ...prev,
            roles: updatedRoles,
            primary_role: updatedRoles.sort((a, b) => b.priority - a.priority)[0] || null
          }));
        } catch (roleError) {
          console.error('Error updating roles:', roleError);
        }

        // Erfolg: Edit-Modus beenden (Erfolgs-Feedback kommt von der Parent-Komponente)
        setEditMode(false);
      } else {
        setConfirmModal({
          visible: true,
          type: 'error',
          title: 'Fehler',
          message: result.error || 'Fehler beim Speichern',
          variant: 'single',
          onConfirm: () => {},
        });
      }
    } catch (err: any) {
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: err.message || 'Unbekannter Fehler',
        variant: 'single',
        onConfirm: () => {},
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const formattedMember = {
      ...initialMember,
      geb_am: initialMember.geb_am ? initialMember.geb_am.split('T')[0] : '',
      eintritt: initialMember.eintritt ? initialMember.eintritt.split('T')[0] : ''
    };
    setMember(formattedMember);
    setMemberRoles(initialMember.roles ? initialMember.roles.map((r: any) => r.id) : []);
    setEditMode(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Nicht angegeben';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Nicht angegeben';
    return date.toLocaleDateString('de-DE');
  };

  const calculateMembershipDuration = (dateString: string) => {
    if (!dateString) return '';
    const startDate = new Date(dateString);
    if (isNaN(startDate.getTime())) return '';

    const now = new Date();
    const years = now.getFullYear() - startDate.getFullYear();
    const months = now.getMonth() - startDate.getMonth();

    let totalYears = years;
    let totalMonths = months;

    if (months < 0) {
      totalYears--;
      totalMonths += 12;
    }

    if (totalYears === 0 && totalMonths === 0) {
      return 'Neu';
    } else if (totalYears === 0) {
      return `${totalMonths} ${totalMonths === 1 ? 'Monat' : 'Monate'}`;
    } else if (totalMonths === 0) {
      return `${totalYears} ${totalYears === 1 ? 'Jahr' : 'Jahre'}`;
    } else {
      return `${totalYears} ${totalYears === 1 ? 'Jahr' : 'Jahre'}, ${totalMonths} ${totalMonths === 1 ? 'Monat' : 'Monate'}`;
    }
  };

  const getRoleBadgeColor = (roleName: string) => {
    switch(roleName) {
      case 'board': return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300';
      case 'trainer': return 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-300';
      case 'auditor': return 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300';
      case 'member': return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-300';
      case 'guest': return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getRoleIcon = (roleName: string) => {
    switch(roleName) {
      case 'board': return AcademicCapIcon;
      case 'trainer': return MegaphoneIcon;
      case 'auditor': return ClipboardDocumentCheckIcon;
      case 'member': return UserGroupIcon;
      case 'guest': return UserPlusIcon;
      default: return UserGroupIcon;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={handleClose}
      />

      {/* Modal - Mobile: Slide-up from bottom, Desktop: Centered */}
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 pointer-events-none">
        <div className="relative w-full sm:max-w-lg pointer-events-auto">
          <div className={`
            relative bg-white dark:bg-gray-800
            rounded-t-2xl sm:rounded-2xl
            shadow-2xl
            max-h-[90vh]
            flex flex-col
            transform transition-all duration-300 ease-in-out
            ${isVisible ? 'translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-0 sm:scale-95 opacity-0'}
          `}>

            {/* Header with role-based background */}
            {(() => {
              const primaryRole = member.roles?.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0))[0];
              let headerBgClass = 'bg-gray-50 dark:bg-gray-700/30';

              if (member.active !== 1) {
                // Inaktiv = Rot
                headerBgClass = 'bg-error-100 dark:bg-error-900/40';
              } else if (primaryRole) {
                switch(primaryRole.name) {
                  case 'board':
                    headerBgClass = 'bg-primary-100 dark:bg-primary-900/40';
                    break;
                  case 'trainer':
                    headerBgClass = 'bg-info-100 dark:bg-info-900/40';
                    break;
                  case 'auditor':
                    headerBgClass = 'bg-warning-100 dark:bg-warning-900/40';
                    break;
                  case 'member':
                    headerBgClass = 'bg-success-100 dark:bg-success-900/40';
                    break;
                  case 'guest':
                    headerBgClass = 'bg-amber-100 dark:bg-amber-900/40';
                    break;
                }
              }

              return (
                <div className={`p-3 sm:p-4 flex-shrink-0 ${headerBgClass} rounded-t-2xl sm:rounded-t-2xl`}>
              <div className="flex items-start gap-2.5 sm:gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {(() => {
                    // Bestimme die Avatar-Ring-Farbe basierend auf Status und Rolle
                    const primaryRole = member.roles?.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0))[0];
                    let borderColorStyle = '#6b7280'; // gray-600 default
                    let avatarBgClass = 'bg-gray-600 dark:bg-gray-600';
                    let textColorClass = 'text-white';

                    if (member.active !== 1) {
                      // Inaktiv = Rot
                      borderColorStyle = '#ef4444'; // red-500
                      avatarBgClass = 'bg-error-500 dark:bg-error-600';
                      textColorClass = 'text-white';
                    } else if (primaryRole) {
                      switch(primaryRole.name) {
                        case 'board':
                          borderColorStyle = '#a855f7'; // primary-500
                          avatarBgClass = 'bg-primary-600 dark:bg-primary-700';
                          break;
                        case 'trainer':
                          borderColorStyle = '#3b82f6'; // info-500
                          avatarBgClass = 'bg-info-600 dark:bg-info-700';
                          break;
                        case 'auditor':
                          borderColorStyle = '#eab308'; // yellow-500
                          avatarBgClass = 'bg-warning-500 dark:bg-warning-600';
                          textColorClass = 'text-warning-900';
                          break;
                        case 'member':
                          borderColorStyle = '#22c55e'; // success-500
                          avatarBgClass = 'bg-success-600 dark:bg-success-700';
                          break;
                        case 'guest':
                          borderColorStyle = '#f59e0b'; // amber-500
                          avatarBgClass = 'bg-amber-500 dark:bg-amber-600';
                          textColorClass = 'text-amber-900';
                          break;
                      }
                    }

                    return profileImage && !imageError ? (
                      <img
                        src={profileImage}
                        alt={`${member.vorname} ${member.name}`}
                        className="w-24 h-24 rounded-xl object-cover border-[3px] hidden sm:block sm:cursor-pointer sm:hover:opacity-90 sm:transition-opacity"
                        style={{ borderColor: borderColorStyle }}
                        onError={() => setImageError(true)}
                        onClick={() => setShowImageModal(true)}
                      />
                    ) : null;
                    // Mobile: kleineres rundes Bild ohne Klick
                  })()}
                  {(() => {
                    const primaryRole = member.roles?.sort((a: any, b: any) => (b.priority || 0) - (a.priority || 0))[0];
                    let borderColorStyle = '#6b7280';
                    let avatarBgClass = 'bg-gray-600 dark:bg-gray-600';
                    let textColorClass = 'text-white';

                    if (member.active !== 1) {
                      borderColorStyle = '#ef4444';
                      avatarBgClass = 'bg-error-500 dark:bg-error-600';
                    } else if (primaryRole) {
                      switch(primaryRole.name) {
                        case 'board': borderColorStyle = '#a855f7'; avatarBgClass = 'bg-primary-600 dark:bg-primary-700'; break;
                        case 'trainer': borderColorStyle = '#3b82f6'; avatarBgClass = 'bg-info-600 dark:bg-info-700'; break;
                        case 'auditor': borderColorStyle = '#eab308'; avatarBgClass = 'bg-warning-500 dark:bg-warning-600'; textColorClass = 'text-warning-900'; break;
                        case 'member': borderColorStyle = '#22c55e'; avatarBgClass = 'bg-success-600 dark:bg-success-700'; break;
                        case 'guest': borderColorStyle = '#f59e0b'; avatarBgClass = 'bg-amber-500 dark:bg-amber-600'; textColorClass = 'text-amber-900'; break;
                      }
                    }

                    // Mobile: kleineres rundes Bild
                    if (profileImage && !imageError) {
                      return (
                        <img
                          src={profileImage}
                          alt={`${member.vorname} ${member.name}`}
                          className="w-12 h-12 rounded-full object-cover border-[2px] sm:hidden"
                          style={{ borderColor: borderColorStyle }}
                          onError={() => setImageError(true)}
                        />
                      );
                    }
                    // Fallback Avatar (Desktop + Mobile)
                    return (
                      <>
                        {/* Desktop Fallback */}
                        <div
                          className={`w-24 h-24 rounded-xl ${avatarBgClass} items-center justify-center border-[3px] hidden sm:flex sm:cursor-pointer sm:hover:opacity-90 sm:transition-opacity`}
                          style={{ borderColor: borderColorStyle }}
                          onClick={() => setShowImageModal(true)}
                        >
                          <UserIcon className={`w-12 h-12 ${textColorClass}`} />
                        </div>
                        {/* Mobile Fallback */}
                        <div
                          className={`w-12 h-12 rounded-full ${avatarBgClass} flex items-center justify-center border-[2px] sm:hidden`}
                          style={{ borderColor: borderColorStyle }}
                        >
                          <UserIcon className={`w-6 h-6 ${textColorClass}`} />
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Name & Meta */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                    {member.vorname} {member.name}
                  </h3>
                  <p className="text-xs sm:text-base text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
                    Nr. {member.mitglied_nr}
                    {member.primary_role?.display_name && (
                      <span className="ml-1">| {member.primary_role.display_name}</span>
                    )}
                  </p>
                  {/* Status Tags */}
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-1.5 sm:mt-2 flex-wrap">
                    {member.active !== 1 ? (
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
                        Inaktiv
                      </span>
                    ) : primaryRole && (
                      <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded ${getRoleBadgeColor(primaryRole.name)}`}>
                        {primaryRole.description}
                      </span>
                    )}
                    {member.active === 1 && memberPayments?.last_payment_date && (
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                        Bezahlt
                      </span>
                    )}
                    {member.active === 1 && !memberPayments?.last_payment_date && memberPayments && (
                      <span className="px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider rounded bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400">
                        Offen
                      </span>
                    )}
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
                </div>
              );
            })()}

            {/* Body - Scrollable Content */}
            <div className="px-3.5 pb-4 sm:px-5 sm:pb-5 space-y-4 sm:space-y-6 overflow-y-auto flex-1 min-h-0 modal-scroll">

              {/* Deaktiviert Banner für inaktive Mitglieder */}
              {!editMode && member.active !== 1 && (
                <div className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg sm:rounded-xl text-gray-600 dark:text-gray-400">
                  <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-xs sm:text-sm">
                    Deaktiviert am {member.deactivated_at
                      ? new Date(member.deactivated_at).toLocaleDateString('de-DE')
                      : 'unbekannt'}
                  </span>
                </div>
              )}

              {editMode ? (
                // Edit Mode Form
                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
                  {/* Persönliche Daten */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <UserIcon className="w-3.5 h-3.5" />
                      Persönliche Daten
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Mitgliedsnummer</label>
                        <input
                          type="text"
                          name="mitglied_nr"
                          value={member.mitglied_nr || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Eintrittsdatum</label>
                        <input
                          type="date"
                          name="eintritt"
                          value={member.eintritt}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Vorname</label>
                        <input
                          type="text"
                          name="vorname"
                          value={member.vorname}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nachname</label>
                        <input
                          type="text"
                          name="name"
                          value={member.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Geburtsdatum</label>
                        <input
                          type="date"
                          name="geb_am"
                          value={member.geb_am}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Geschlecht</label>
                        <select
                          name="gender"
                          value={member.gender !== null && member.gender !== undefined ? String(member.gender) : ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        >
                          <option value="">Nicht angegeben</option>
                          <option value="0">Männlich</option>
                          <option value="1">Weiblich</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Adresse & Kontakt */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <MapPinIcon className="w-3.5 h-3.5" />
                      Adresse & Kontakt
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Straße</label>
                        <input
                          type="text"
                          name="strasse"
                          value={member.strasse || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">PLZ</label>
                        <input
                          type="text"
                          name="plz"
                          value={member.plz || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Ort</label>
                        <input
                          type="text"
                          name="ort"
                          value={member.ort || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Telefon</label>
                        <input
                          type="tel"
                          name="tel_nummer"
                          value={member.tel_nummer || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">E-Mail</label>
                        <input
                          type="email"
                          name="email"
                          value={member.email || ''}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-info-500 focus:border-info-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Rollenverwaltung */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <ShieldCheckIcon className="w-3.5 h-3.5" />
                      Rollenverwaltung
                    </h4>
                    <div className="space-y-2">
                      {/* Active Status */}
                      <div
                        onClick={() => setMember((prev: any) => ({ ...prev, active: prev.active === 1 ? 0 : 1 }))}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          member.active === 1
                            ? 'bg-info-50 dark:bg-info-900/20 border-2 border-info-500 dark:border-info-600'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className={`w-4 h-4 ${member.active === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${member.active === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                            Aktives Mitglied
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          member.active === 1
                            ? 'bg-info-500 dark:bg-info-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {member.active === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>

                      {/* Roles */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {availableRoles.map((role) => {
                          const IconComponent = getRoleIcon(role.name);
                          const isSelected = memberRoles.includes(role.id);

                          // Farben basierend auf Rolle
                          let selectedBg = 'bg-gray-100 dark:bg-gray-700';
                          let selectedBorder = 'border-gray-400 dark:border-gray-500';
                          let selectedText = 'text-gray-700 dark:text-gray-300';
                          let iconColor = 'text-gray-400 dark:text-gray-500';
                          let checkBg = 'bg-gray-500';

                          if (isSelected) {
                            switch(role.name) {
                              case 'board':
                                selectedBg = 'bg-primary-50 dark:bg-primary-900/20';
                                selectedBorder = 'border-primary-500 dark:border-primary-600';
                                selectedText = 'text-primary-700 dark:text-primary-300';
                                iconColor = 'text-primary-600 dark:text-primary-400';
                                checkBg = 'bg-primary-500 dark:bg-primary-600';
                                break;
                              case 'trainer':
                                selectedBg = 'bg-info-50 dark:bg-info-900/20';
                                selectedBorder = 'border-info-500 dark:border-info-600';
                                selectedText = 'text-info-700 dark:text-info-300';
                                iconColor = 'text-info-600 dark:text-info-400';
                                checkBg = 'bg-info-500 dark:bg-info-600';
                                break;
                              case 'auditor':
                                selectedBg = 'bg-warning-50 dark:bg-warning-900/20';
                                selectedBorder = 'border-warning-500 dark:border-warning-600';
                                selectedText = 'text-warning-700 dark:text-warning-300';
                                iconColor = 'text-warning-600 dark:text-warning-400';
                                checkBg = 'bg-warning-500 dark:bg-warning-600';
                                break;
                              case 'member':
                                selectedBg = 'bg-success-50 dark:bg-success-900/20';
                                selectedBorder = 'border-success-500 dark:border-success-600';
                                selectedText = 'text-success-700 dark:text-success-300';
                                iconColor = 'text-success-600 dark:text-success-400';
                                checkBg = 'bg-success-500 dark:bg-success-600';
                                break;
                              case 'guest':
                                selectedBg = 'bg-gray-100 dark:bg-gray-700';
                                selectedBorder = 'border-gray-500 dark:border-gray-500';
                                selectedText = 'text-gray-700 dark:text-gray-300';
                                iconColor = 'text-gray-600 dark:text-gray-400';
                                checkBg = 'bg-gray-500 dark:bg-gray-600';
                                break;
                            }
                          }

                          return (
                            <div
                              key={role.id}
                              onClick={() => toggleRole(role.id)}
                              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? `${selectedBg} border-2 ${selectedBorder}`
                                  : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <IconComponent className={`w-4 h-4 ${isSelected ? iconColor : 'text-gray-400 dark:text-gray-500'}`} />
                                <span className={`text-sm font-medium ${isSelected ? selectedText : 'text-gray-900 dark:text-white'}`}>
                                  {role.description}
                                </span>
                              </div>
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                isSelected ? checkBg : 'bg-gray-200 dark:bg-gray-600'
                              }`}>
                                {isSelected && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Datenschutz-Parameter */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <DocumentCheckIcon className="w-3.5 h-3.5" />
                      Datenschutz-Parameter
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* Datenschutzerklärung */}
                      <div
                        onClick={() => setMember((prev: any) => ({ ...prev, datenschutzerklaerung: prev.datenschutzerklaerung === 1 ? 0 : 1 }))}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          member.datenschutzerklaerung === 1
                            ? 'bg-success-50 dark:bg-success-900/20 border-2 border-success-500 dark:border-success-600'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <ShieldCheckIcon className={`w-4 h-4 ${member.datenschutzerklaerung === 1 ? 'text-success-600 dark:text-success-400' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${member.datenschutzerklaerung === 1 ? 'text-success-700 dark:text-success-300' : 'text-gray-900 dark:text-white'}`}>
                            Datenschutz
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          member.datenschutzerklaerung === 1
                            ? 'bg-success-500 dark:bg-success-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {member.datenschutzerklaerung === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>

                      {/* Homepage */}
                      <div
                        onClick={() => setMember((prev: any) => ({ ...prev, homepage: prev.homepage === 1 ? 0 : 1 }))}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          member.homepage === 1
                            ? 'bg-info-50 dark:bg-info-900/20 border-2 border-info-500 dark:border-info-600'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <GlobeAltIcon className={`w-4 h-4 ${member.homepage === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${member.homepage === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                            Homepage
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          member.homepage === 1
                            ? 'bg-info-500 dark:bg-info-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {member.homepage === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>

                      {/* Facebook */}
                      <div
                        onClick={() => setMember((prev: any) => ({ ...prev, facebook: prev.facebook === 1 ? 0 : 1 }))}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          member.facebook === 1
                            ? 'bg-info-50 dark:bg-info-900/20 border-2 border-info-500 dark:border-info-600'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <ChatBubbleLeftRightIcon className={`w-4 h-4 ${member.facebook === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${member.facebook === 1 ? 'text-info-700 dark:text-info-300' : 'text-gray-900 dark:text-white'}`}>
                            Facebook
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          member.facebook === 1
                            ? 'bg-info-500 dark:bg-info-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {member.facebook === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>

                      {/* Presse */}
                      <div
                        onClick={() => setMember((prev: any) => ({ ...prev, presse: prev.presse === 1 ? 0 : 1 }))}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          member.presse === 1
                            ? 'bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 dark:border-amber-600'
                            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <NewspaperIcon className={`w-4 h-4 ${member.presse === 1 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`} />
                          <span className={`text-sm font-medium ${member.presse === 1 ? 'text-amber-700 dark:text-amber-300' : 'text-gray-900 dark:text-white'}`}>
                            Presse
                          </span>
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                          member.presse === 1
                            ? 'bg-amber-500 dark:bg-amber-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {member.presse === 1 && <CheckCircleIcon className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                // View Mode
                <>
                  {/* Persönliche Daten */}
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
                      <UserIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Persönliche Daten
                    </h4>
                    <div className="space-y-1.5 sm:space-y-3">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-500 dark:text-gray-500">Name</span>
                        <span className="text-gray-900 dark:text-white">{member.name}, {member.vorname}</span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-500 dark:text-gray-500">Geburtsdatum</span>
                        <span className="text-gray-900 dark:text-white">{formatDate(member.geb_am)}</span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-500 dark:text-gray-500">Geschlecht</span>
                        <span className="text-gray-900 dark:text-white">
                          {String(member.gender) === '0' ? 'Männlich'
                           : String(member.gender) === '1' ? 'Weiblich'
                           : <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Adresse & Kontakt */}
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
                      <MapPinIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Adresse & Kontakt
                    </h4>
                    <div className="space-y-1.5 sm:space-y-3">
                      <div className="flex justify-between text-sm sm:text-base gap-2">
                        <span className="text-gray-500 dark:text-gray-500 flex-shrink-0">Adresse</span>
                        <span className="text-gray-900 dark:text-white text-right">
                          {member.strasse && member.plz && member.ort
                            ? `${member.strasse}, ${member.plz} ${member.ort}`
                            : <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-500 dark:text-gray-500">Telefon</span>
                        {member.tel_nummer ? (
                          <a href={`tel:${member.tel_nummer}`} className="text-info-500 dark:text-info-400 hover:underline">{member.tel_nummer}</a>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                        )}
                      </div>
                      <div className="flex justify-between text-sm sm:text-base gap-2">
                        <span className="text-gray-500 dark:text-gray-500 flex-shrink-0">E-Mail</span>
                        {member.email ? (
                          <a href={`mailto:${member.email}`} className="text-info-500 dark:text-info-400 hover:underline break-all text-right">{member.email}</a>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-500 italic">Nicht angegeben</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Vereinsdaten & Rollen */}
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
                      <BuildingOfficeIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Vereinsdaten & Rollen
                    </h4>
                    <div className="space-y-1.5 sm:space-y-3">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-gray-500 dark:text-gray-500">Mitglied seit</span>
                        <span className="text-gray-900 dark:text-white">
                          {formatDate(member.eintritt)}
                          {member.eintritt && calculateMembershipDuration(member.eintritt) && (
                            <span className="text-gray-500 dark:text-gray-500 ml-1 hidden sm:inline">
                              ({calculateMembershipDuration(member.eintritt)})
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base items-center">
                        <span className="text-gray-500 dark:text-gray-500">Rollen</span>
                        {member.roles && member.roles.length > 0 ? (
                          <div className="flex flex-wrap gap-1 sm:gap-1.5 justify-end">
                            {member.roles.map((role: any) => {
                              const IconComponent = getRoleIcon(role.name);
                              return (
                                <span
                                  key={role.id}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 text-xs sm:text-sm font-medium rounded-full ${getRoleBadgeColor(role.name)}`}
                                >
                                  <IconComponent className="w-3 h-3 sm:w-4 sm:h-4" />
                                  {role.description}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-500 italic">Keine Rollen</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Section Divider */}
                  <hr className="border-gray-200 dark:border-gray-700" />

                  {/* Datenschutz-Parameter */}
                  <div className="space-y-2 sm:space-y-3">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5 sm:gap-2">
                      <DocumentCheckIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Datenschutz-Parameter
                    </h4>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <ShieldCheckIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${member.datenschutzerklaerung === 1 ? 'text-success-500' : 'text-gray-400'}`} />
                        <span className={`text-xs sm:text-base ${member.datenschutzerklaerung === 1 ? 'text-success-600 dark:text-success-400' : 'text-gray-500 dark:text-gray-500'}`}>
                          Datenschutz {member.datenschutzerklaerung === 1 ? 'Ja' : 'Nein'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <GlobeAltIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${member.homepage === 1 ? 'text-info-500' : 'text-gray-400'}`} />
                        <span className={`text-xs sm:text-base ${member.homepage === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-500 dark:text-gray-500'}`}>
                          Homepage {member.homepage === 1 ? 'Ja' : 'Nein'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <ChatBubbleLeftRightIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${member.facebook === 1 ? 'text-info-500' : 'text-gray-400'}`} />
                        <span className={`text-xs sm:text-base ${member.facebook === 1 ? 'text-info-600 dark:text-info-400' : 'text-gray-500 dark:text-gray-500'}`}>
                          Facebook {member.facebook === 1 ? 'Ja' : 'Nein'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <NewspaperIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${member.presse === 1 ? 'text-amber-500' : 'text-gray-400'}`} />
                        <span className={`text-xs sm:text-base ${member.presse === 1 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-500'}`}>
                          Presse {member.presse === 1 ? 'Ja' : 'Nein'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Section Divider - Hunde */}
                  {dogsStatus && dogsStatus.hasDogs && <hr className="border-gray-200 dark:border-gray-700" />}

                  {/* Hunde */}
                  {dogsStatus && dogsStatus.hasDogs && (
                    <div className="space-y-2 sm:space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                          <HeartIcon className="w-3.5 h-3.5" />
                          Meine Hunde
                        </h4>
                        <span className="text-xs text-gray-500 dark:text-gray-500">
                          {dogsStatus.count} {dogsStatus.count === 1 ? 'Hund' : 'Hunde'}
                        </span>
                      </div>
                      <div className="space-y-1.5 sm:space-y-2">
                        {dogsStatus.dogs && dogsStatus.dogs.map((dog, index) => {
                          const dogId = dog.id || '';
                          const dogImageUrl = dogId ? getDogImageUrl(Number(dogId), 'M') : '';
                          const hasImageError = dogImageErrors[dogId] || false;

                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-600"
                            >
                              <div className="flex items-center gap-2.5 sm:gap-3">
                                {/* Dog Image/Icon */}
                                {dogImageUrl && !hasImageError ? (
                                  <img
                                    src={dogImageUrl}
                                    alt={dog.name}
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover"
                                    onError={() => {
                                      setDogImageErrors(prev => ({ ...prev, [dogId]: true }));
                                    }}
                                  />
                                ) : (
                                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                                    <HeartIcon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500 dark:text-amber-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">{dog.name}</p>
                                  <div className="flex gap-1 sm:gap-1.5 mt-0.5">
                                    {dog.valid_insurance ? (
                                      <span className="px-1 py-0.5 text-[10px] sm:text-xs bg-success-500/20 text-success-600 dark:text-success-400 rounded">Versichert</span>
                                    ) : (
                                      <span className="px-1 py-0.5 text-[10px] sm:text-xs bg-gray-500/20 text-gray-500 dark:text-gray-400 rounded">Keine Vers.</span>
                                    )}
                                    {dog.valid_tollwut ? (
                                      <span className="px-1 py-0.5 text-[10px] sm:text-xs bg-info-500/20 text-info-600 dark:text-info-400 rounded">Tollwut</span>
                                    ) : (
                                      <span className="px-1 py-0.5 text-[10px] sm:text-xs bg-gray-500/20 text-gray-500 dark:text-gray-400 rounded">Keine Tollw.</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Section Divider - Beitrag */}
                  {memberPayments && <hr className="border-gray-200 dark:border-gray-700" />}

                  {/* Beitrag */}
                  {memberPayments && (
                    <div className="space-y-2 sm:space-y-3">
                      <h4 className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <CurrencyEuroIcon className="w-3.5 h-3.5" />
                        Mitgliedsbeitrag {new Date().getFullYear()}
                      </h4>
                      <div className="p-2.5 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-600 space-y-1.5 sm:space-y-2">
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span className="text-gray-500 dark:text-gray-500">Status</span>
                          <span className={`font-medium ${memberPayments.last_payment_date ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                            {memberPayments.last_payment_date ? 'Bezahlt' : 'Offen'}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs sm:text-sm">
                          <span className="text-gray-500 dark:text-gray-500">Betrag</span>
                          <span className={`font-semibold ${memberPayments.last_payment_date ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                            {memberPayments.amount} EUR
                          </span>
                        </div>
                        {memberPayments.last_payment_date && (
                          <div className="flex justify-between text-xs sm:text-sm">
                            <span className="text-gray-500 dark:text-gray-500">Gezahlt am</span>
                            <span className="text-gray-900 dark:text-white">
                              {new Date(memberPayments.last_payment_date).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 sm:p-5 border-t border-gray-200 dark:border-gray-700 space-y-2 sm:space-y-3 flex-shrink-0">
              {editMode ? (
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={handleCancel}
                    className="flex-1 py-2.5 sm:py-3 text-gray-700 dark:text-gray-300 font-medium rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 py-2.5 sm:py-3 bg-info-600 text-white font-medium rounded-lg hover:bg-info-700 transition-all text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Wird gespeichert...' : 'Speichern'}
                  </button>
                </div>
              ) : member.active !== 1 ? (
                // Inaktiv: Reaktivieren, Endgültig löschen, Schließen
                <>
                  {/* Row 1: Reaktivieren */}
                  <button
                    onClick={() => setActivateConfirmModal(true)}
                    className="w-full py-2.5 sm:py-3 bg-warning-500 hover:bg-warning-600 text-white rounded-lg text-sm sm:text-base font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <ArrowPathIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    Reaktivieren
                  </button>
                  {/* Row 2: Endgültig löschen & Schließen */}
                  <div className="flex gap-2 sm:gap-3">
                    {onDelete && (
                      <button
                        onClick={() => setDeleteConfirmModal(true)}
                        className="flex-1 py-2.5 sm:py-3 bg-error-500 hover:bg-error-600 text-white rounded-lg text-sm sm:text-base font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        Endgültig löschen
                      </button>
                    )}
                    <button
                      onClick={handleClose}
                      className="flex-1 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm sm:text-base font-medium border border-gray-200 dark:border-gray-600 transition-colors"
                    >
                      Schließen
                    </button>
                  </div>
                </>
              ) : (
                // Aktiv: Buchungshistorie & Deaktivieren nebeneinander, Schließen & Bearbeiten
                <>
                  {/* Row 1: Buchungshistorie & Deaktivieren nebeneinander auf Mobile */}
                  <div className="flex gap-2 sm:hidden">
                    <button
                      onClick={() => setShowBookingHistory(true)}
                      className="flex-1 py-2.5 bg-info-50 dark:bg-info-900/20 hover:bg-info-100 dark:hover:bg-info-900/30 text-info-700 dark:text-info-300 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors border border-info-200 dark:border-info-800"
                    >
                      <CalendarIcon className="w-4 h-4" />
                      Buchungen
                    </button>
                    <button
                      onClick={() => setActivateConfirmModal(true)}
                      className="flex-1 py-2.5 bg-warning-50 dark:bg-warning-900/20 hover:bg-warning-100 dark:hover:bg-warning-900/30 text-warning-700 dark:text-warning-300 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition-colors border border-warning-200 dark:border-warning-800"
                    >
                      <XCircleIcon className="w-4 h-4" />
                      Deaktivieren
                    </button>
                  </div>
                  {/* Desktop: original stacked layout */}
                  <button
                    onClick={() => setShowBookingHistory(true)}
                    className="hidden sm:flex w-full py-3 bg-info-50 dark:bg-info-900/20 hover:bg-info-100 dark:hover:bg-info-900/30 text-info-700 dark:text-info-300 rounded-lg text-base font-medium items-center justify-center gap-2 transition-colors border border-info-200 dark:border-info-800"
                  >
                    <CalendarIcon className="w-5 h-5" />
                    Buchungshistorie
                  </button>
                  <button
                    onClick={() => setActivateConfirmModal(true)}
                    className="hidden sm:flex w-full py-3 bg-warning-50 dark:bg-warning-900/20 hover:bg-warning-100 dark:hover:bg-warning-900/30 text-warning-700 dark:text-warning-300 rounded-lg text-base font-medium items-center justify-center gap-2 transition-colors border border-warning-200 dark:border-warning-800"
                  >
                    <XCircleIcon className="w-5 h-5" />
                    Deaktivieren
                  </button>
                  {/* Row 2 (mobile) / Row 3 (desktop): Schließen & Bearbeiten */}
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      onClick={handleClose}
                      className="flex-1 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm sm:text-base font-medium border border-gray-200 dark:border-gray-600 transition-colors"
                    >
                      Schließen
                    </button>
                    {onSave && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="flex-1 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm sm:text-base font-medium border border-gray-200 dark:border-gray-600 flex items-center justify-center gap-1.5 sm:gap-2 transition-colors"
                      >
                        <PencilIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        Bearbeiten
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={deleteConfirmModal}
        type="warning"
        title="Mitglied löschen"
        message={`Möchten Sie ${member.vorname} ${member.name} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setDeleteConfirmModal(false)
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              if (onDelete) {
                onDelete(member.mg_nr);
                handleClose();
              }
              setDeleteConfirmModal(false);
            }
          }
        }}
      />

      {/* Activate/Deactivate Confirmation Modal */}
      <ConfirmationModal
        visible={activateConfirmModal}
        type="question"
        title={member.active === 1 ? "Mitglied deaktivieren?" : "Mitglied aktivieren?"}
        message={member.active === 1
          ? `Möchten Sie ${member.vorname} ${member.name} wirklich deaktivieren?`
          : `Möchten Sie ${member.vorname} ${member.name} wieder aktivieren?`
        }
        variant="two"
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setActivateConfirmModal(false)
          },
          confirm: {
            text: member.active === 1 ? 'Deaktivieren' : 'Aktivieren',
            onPress: async () => {
              setLoading(true);
              try {
                const newActiveStatus = member.active === 1 ? 0 : 1;

                const response = await apiClient.put(`/api/members/${member.mg_nr}`, {
                  active: newActiveStatus
                });

                if (response.data.success) {
                  const updatedMember = {
                    ...member,
                    active: newActiveStatus
                  };
                  setMember(updatedMember);

                  setConfirmModal({
                    visible: true,
                    type: 'success',
                    title: 'Erfolgreich',
                    message: newActiveStatus === 0
                      ? 'Mitglied wurde deaktiviert.'
                      : 'Mitglied wurde aktiviert.',
                    variant: 'single',
                    onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                  });
                } else {
                  setConfirmModal({
                    visible: true,
                    type: 'error',
                    title: 'Fehler',
                    message: 'Status konnte nicht geändert werden.',
                    variant: 'single',
                    onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                  });
                }
              } catch (error: any) {
                console.error('Error updating member status:', error);
                setConfirmModal({
                  visible: true,
                  type: 'error',
                  title: 'Fehler',
                  message: `Fehler beim Ändern des Status: ${error.message}`,
                  variant: 'single',
                  onConfirm: () => setConfirmModal({ ...confirmModal, visible: false })
                });
              } finally {
                setLoading(false);
                setActivateConfirmModal(false);
              }
            }
          }
        }}
      />

      {/* General Confirmation Modal for Success/Error */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, visible: false });
          }
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

      {/* Booking History Modal */}
      {showBookingHistory && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowBookingHistory(false)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Buchungshistorie
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {member.vorname} {member.name}
                </p>
              </div>
              <button
                onClick={() => setShowBookingHistory(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {loadingBookings ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : bookingHistory.length === 0 ? (
                <div className="text-center py-12">
                  <CalendarIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    Keine Buchungen gefunden
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Dieses Mitglied hat noch an keinen Events teilgenommen.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="bg-info-50 dark:bg-info-900/20 rounded-xl p-4 mb-4 border border-info-200 dark:border-info-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-info-100 dark:bg-info-900/40 rounded-lg flex items-center justify-center">
                        <CalendarIcon className="h-5 w-5 text-info-600 dark:text-info-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-info-700 dark:text-info-300">
                          {bookingHistory.length}
                        </p>
                        <p className="text-sm text-info-600 dark:text-info-400">
                          {bookingHistory.length === 1 ? 'Teilnahme' : 'Teilnahmen'} gesamt
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Bookings List */}
                  {bookingHistory.map((booking, index) => (
                    <div
                      key={booking.id || index}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                            {booking.title || booking.event_title || 'Unbenanntes Event'}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                            <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                            <span>
                              {booking.event_date
                                ? new Date(booking.event_date).toLocaleDateString('de-DE', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  })
                                : 'Datum unbekannt'}
                            </span>
                          </div>
                          {booking.event_time && (
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500 dark:text-gray-400">
                              <ClockIcon className="h-4 w-4 flex-shrink-0" />
                              <span>{booking.event_time}{booking.end_time ? ` - ${booking.end_time}` : ''} Uhr</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed' || booking.status === 1
                              ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                              : booking.status === 'cancelled' || booking.status === 0
                              ? 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300'
                          }`}>
                            {booking.status === 'confirmed' || booking.status === 1
                              ? 'Teilgenommen'
                              : booking.status === 'cancelled' || booking.status === 0
                              ? 'Abgesagt'
                              : 'Gebucht'}
                          </span>
                        </div>
                      </div>
                      {booking.trainer_name && (
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400">
                          Trainer: {booking.trainer_name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-5 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowBookingHistory(false)}
                className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal - nur Desktop */}
      {showImageModal && profileImage && (
        <div
          className="fixed inset-0 bg-black/95 z-[60] hidden sm:flex items-center justify-center"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative w-full h-full flex items-center justify-center p-8">
            <img
              src={profileImage.replace('size=S', 'size=L').replace('size=M', 'size=L')}
              alt={`${member.vorname} ${member.name}`}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            />
            <button
              onClick={(e) => { e.stopPropagation(); setShowImageModal(false); }}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 backdrop-blur rounded-full flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MemberDetail;